import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { SmartLink as Link } from "./SmartLink";
import {
  ArrowUp,
  Building2,
  Calendar,
  ChevronDown,
  Compass,
  ExternalLink,
  Layers,
  Mail,
  Map,
  MapPin,
  MessageCircle,
  Phone,
  Sparkles,
  X,
} from "lucide-react";

import LanguageSwitcher from "./LanguageSwitcher";
import Wordmark from "./Wordmark";
import { t } from "@/lib/i18n";

/**
 * Mobile menu — JTR-inspired layered design.
 *
 * Structure (top → bottom):
 *   1. Sticky header with logo, language switcher, close
 *   2. QUICK LINKS card (navy) — 4 primary destinations + mega Contact CTA
 *   3. Pastel icon-card grid — 4 secondary destinations
 *   4. OPERATED SERVICES segmented switcher (3 service brands)
 *   5. Accordion sections (About / Site Updates / Company) with colored dots
 *   6. Direct-contact tile (phone / email / office)
 *   7. Sticky bottom CTA bar (Services · Contact · Top)
 *
 * Honors: body-scroll-lock, ESC close, safe-area insets, prefers-reduced-motion.
 */
export default function MobileMenu({ lang, open, onClose }) {
  const isJa = lang === "ja";
  const location = useLocation();
  const [expanded, setExpanded] = useState({
    about: false,
    updates: false,
    company: false,
  });

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const link = (path) => `/${lang}${path}`;
  const isActive = (path) => location.pathname === link(path);

  const toggle = (key) =>
    setExpanded((s) => ({ ...s, [key]: !s[key] }));

  const quickLinks = [
    { key: "about", label: t(lang, "nav.about"), path: "/about", accent: "#5B2E83" },
    { key: "services", label: t(lang, "nav.services"), path: "/services", accent: "#0E5FB0" },
    { key: "whatsNew", label: t(lang, "nav.whatsNew"), path: "/whats-new", accent: "#2F8F3C" },
    { key: "roadmap", label: t(lang, "nav.roadmap"), path: "/roadmap", accent: "#0E5FB0" },
  ];

  const iconCards = [
    {
      key: "corporateProfile",
      label: t(lang, "nav.corporateProfile"),
      path: "/corporate-profile",
      Icon: Building2,
      tint: "#F1F5F9",
      iconColor: "#0F1D2D",
    },
    {
      key: "signboards",
      label: t(lang, "nav.signboards"),
      path: "/signboards",
      Icon: MapPin,
      tint: "#F3FAEE",
      iconColor: "#2F8F3C",
    },
    {
      key: "pillars",
      label: isJa ? "3つの柱" : "Our Pillars",
      path: "/about#pillars",
      Icon: Layers,
      tint: "#F5F0FA",
      iconColor: "#5B2E83",
    },
    {
      key: "milestones",
      label: isJa ? "沿革" : "Milestones",
      path: "/about#history",
      Icon: Calendar,
      tint: "#FFF5E6",
      iconColor: "#B45309",
    },
  ];

  const services = [
    {
      slug: "japanetccard",
      domain: "JapanETCcard.com",
      short: "JEC",
      accent: "#5B2E83",
      tint: "#F5F0FA",
      path: "/services/japanetccard",
    },
    {
      slug: "japantollreceipts",
      domain: "JapanTollReceipts.jp",
      short: "JTR",
      accent: "#2F8F3C",
      tint: "#F3FAEE",
      path: "/services/japantollreceipts",
    },
    {
      slug: "japanchangemoney",
      domain: "JapanChangeMoney.com",
      short: "JCM",
      accent: "#0E5FB0",
      tint: "#EFF6FF",
      path: "/services/japanchangemoney",
      planned: true,
    },
  ];

  const accordions = [
    {
      key: "about",
      dot: "#5B2E83",
      title: t(lang, "nav.about"),
      subtitle: isJa ? "理念・価値・歩み" : "Vision · Values · History",
      bg: "#FBF7FF",
      items: [
        { label: isJa ? "企業理念" : "Vision", path: "/about#vision" },
        { label: isJa ? "5つの価値" : "Five Values", path: "/about#values" },
        { label: isJa ? "3つの柱" : "Three Pillars", path: "/about#pillars" },
        { label: isJa ? "代表メッセージ" : "Message", path: "/about#message" },
        { label: isJa ? "AI エンジン" : "AI Engine", path: "/about#engine" },
      ],
    },
    {
      key: "updates",
      dot: "#2F8F3C",
      title: isJa ? "最新動向" : "Site Updates",
      subtitle: isJa ? "最新情報・ロードマップ・お客様の声" : "What's New · Roadmap · Voices",
      bg: "#F4FBF1",
      items: [
        { label: t(lang, "nav.whatsNew"), path: "/whats-new" },
        { label: t(lang, "nav.roadmap"), path: "/roadmap" },
        { label: isJa ? "お客様の声" : "Customer Testimonials", path: "/testimonials" },
      ],
    },
    {
      key: "company",
      dot: "#0E5FB0",
      title: isJa ? "コーポレート" : "Company",
      subtitle: isJa
        ? "会社概要・チーム・採用・お問い合わせ"
        : "Profile · Team · Careers · Contact",
      bg: "#EFF6FF",
      items: [
        { label: t(lang, "nav.corporateProfile"), path: "/corporate-profile" },
        { label: isJa ? "経営チーム" : "Leadership", path: "/our-leaders" },
        { label: isJa ? "AI支援ワークフロー" : "Our AI Team", path: "/our-team" },
        {
          label: isJa
            ? "代表メッセージ (JapanETCcard)"
            : "Founder's Message · JapanETCcard",
          path: "/founders-message/japanetccard",
        },
        {
          label: isJa
            ? "代表メッセージ (JapanTollReceipts)"
            : "Founder's Message · JapanTollReceipts",
          path: "/founders-message/japantollreceipts",
        },
        { label: isJa ? "採用情報" : "Careers", path: "/careers" },
        { label: isJa ? "特定商取引法" : "Commercial Notice", path: "/legal/commercial-transactions" },
        { label: t(lang, "nav.contact"), path: "/contact" },
      ],
    },
  ];

  return (
    <div
      data-testid="mobile-menu"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] bg-white lg:hidden overflow-y-auto overscroll-contain animate-brand-fade-in"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 76px)" }}
    >
      {/* Sticky header */}
      <div
        className="px-5 flex items-center justify-between border-b border-[#E2E8F0] bg-white/95 backdrop-blur sticky top-0 z-10"
        style={{
          paddingTop: "max(env(safe-area-inset-top), 12px)",
          paddingBottom: "12px",
        }}
      >
        <span className="min-w-0">
          <Wordmark size="header" tone="navy" showSub={false} />
        </span>
        <div className="flex items-center gap-2.5">
          <LanguageSwitcher lang={lang} />
          <button
            type="button"
            data-testid="mobile-menu-close"
            aria-label={t(lang, "common.close")}
            onClick={onClose}
            className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-[#F1F5F9] border border-[#E2E8F0] text-[#0F1D2D] hover:bg-white hover:border-[#0E5FB0] transition-colors"
          >
            <X className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {/* 1) QUICK LINKS card */}
      <section className="px-4 pt-5" data-testid="mobile-quick-links">
        <div
          className="rounded-3xl p-5 bg-gradient-to-br from-[#5B2E83] via-[#4A2570] to-[#3F1F5B] shadow-[0_24px_60px_-24px_rgba(91,46,131,0.55)] ring-1 ring-white/10"
        >
          <div className="text-center text-[10px] tracking-[0.36em] font-mono-display font-bold text-white/70 uppercase mb-4">
            {isJa ? "クイックリンク" : "Quick Links"}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {quickLinks.map((q) => (
              <Link
                key={q.key}
                to={link(q.path)}
                data-testid={`mobile-quick-${q.key}`}
                className={[
                  "relative flex items-center justify-center min-h-[56px] rounded-2xl px-3 text-[14.5px] font-semibold tracking-tight text-white text-center leading-tight transition-colors overflow-hidden",
                  isActive(q.path)
                    ? "bg-white/20 ring-1 ring-white/40"
                    : "bg-white/10 ring-1 ring-white/10 hover:bg-white/15",
                ].join(" ")}
              >
                <span
                  aria-hidden
                  className="absolute left-0 top-0 bottom-0 w-1"
                  style={{ backgroundColor: q.accent }}
                />
                {q.label}
              </Link>
            ))}
          </div>
          <Link
            to={link("/contact")}
            data-testid="mobile-quick-contact-cta"
            className="mt-4 flex items-center justify-center gap-2 min-h-[56px] rounded-2xl bg-[#78D33C] hover:bg-[#5FB825] text-white text-[15px] font-bold tracking-tight transition-colors"
          >
            <Mail className="h-4 w-4" strokeWidth={2.2} />
            {isJa ? "お問い合わせ" : "Contact us"}
          </Link>
        </div>
      </section>

      {/* 2) Pastel icon-card grid */}
      <section className="px-4 pt-4 grid grid-cols-2 gap-3" data-testid="mobile-icon-cards">
        {iconCards.map((c) => (
          <Link
            key={c.key}
            to={link(c.path)}
            onClick={onClose}
            data-testid={`mobile-icon-card-${c.key}`}
            className="flex flex-col items-center justify-center text-center gap-2 min-h-[120px] rounded-2xl px-4 py-5 ring-1 ring-[#E2E8F0] hover:ring-[#0F1D2D]/30 active:bg-[#F8F9FA] transition-colors"
            style={{ backgroundColor: c.tint }}
          >
            <c.Icon className="h-7 w-7" color={c.iconColor} strokeWidth={1.6} />
            <span className="text-[13.5px] font-semibold tracking-tight text-[#0F1D2D] leading-tight">
              {c.label}
            </span>
          </Link>
        ))}
      </section>

      {/* 3) OPERATED SERVICES segmented switcher */}
      <section className="px-4 pt-6" data-testid="mobile-operated-services">
        <div className="text-[10px] tracking-[0.28em] font-mono-display font-bold text-[#64748B] uppercase mb-2.5 px-1">
          {isJa ? "運営サービス" : "Operated Services"}
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          {services.map((s) => (
            <Link
              key={s.slug}
              to={link(s.path)}
              onClick={onClose}
              data-testid={`mobile-service-${s.slug}`}
              className="flex flex-col items-center justify-center gap-1 min-h-[64px] rounded-xl px-2 py-2 ring-1 ring-[#E2E8F0] text-center transition-colors hover:ring-[#0F1D2D]/30 active:bg-[#F8F9FA]"
              style={{ backgroundColor: s.tint }}
            >
              <span
                aria-hidden
                className="h-2 w-2 rounded-full mb-0.5"
                style={{ backgroundColor: s.accent }}
              />
              <span
                className="text-[15px] font-bold tracking-tight leading-tight font-mono"
                style={{ color: s.accent }}
              >
                {s.short}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 4) Accordion sections */}
      <section className="px-4 pt-6 space-y-2.5" data-testid="mobile-accordions">
        {accordions.map((acc) => {
          const isOpen = expanded[acc.key];
          return (
            <div
              key={acc.key}
              className="rounded-2xl ring-1 ring-[#E2E8F0] overflow-hidden bg-white"
              data-testid={`mobile-accordion-${acc.key}`}
            >
              <button
                type="button"
                onClick={() => toggle(acc.key)}
                aria-expanded={isOpen}
                aria-controls={`mobile-accordion-${acc.key}-panel`}
                className="w-full flex items-center justify-between gap-3 px-4 py-4 min-h-[64px] text-left active:bg-[#F8F9FA]"
                data-testid={`mobile-accordion-${acc.key}-toggle`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: acc.dot }}
                  />
                  <div className="min-w-0">
                    <div
                      className="text-[15.5px] font-bold tracking-tight"
                      style={{ color: acc.dot }}
                    >
                      {acc.title}
                    </div>
                    <div className="text-[11.5px] text-[#64748B] mt-0.5">
                      {acc.subtitle}
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={[
                    "h-4 w-4 text-[#64748B] shrink-0 transition-transform",
                    isOpen ? "rotate-180" : "",
                  ].join(" ")}
                  strokeWidth={2}
                />
              </button>
              {isOpen && (
                <div
                  id={`mobile-accordion-${acc.key}-panel`}
                  role="region"
                  aria-label={acc.title}
                  className="border-t border-[#E2E8F0] divide-y divide-[#E2E8F0]"
                  style={{ backgroundColor: acc.bg }}
                >
                  {acc.items.map((item) => (
                    <Link
                      key={item.path}
                      to={link(item.path)}
                      data-testid={`mobile-accordion-${acc.key}-${item.path.replace(/[^a-z]/gi, "")}`}
                      className="flex items-center justify-between px-4 py-3.5 min-h-[52px] text-[14.5px] font-semibold tracking-tight text-[#0F1D2D] active:bg-white/60"
                    >
                      <span>{item.label}</span>
                      <ExternalLink
                        className="h-3.5 w-3.5 text-[#94A3B8] opacity-70"
                        strokeWidth={2}
                        aria-hidden
                      />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* 5) Direct-contact tile */}
      <div
        className="mt-6 mx-4 rounded-2xl bg-[#F8F9FA] border border-[#E2E8F0] overflow-hidden divide-y divide-[#E2E8F0]"
        data-testid="mobile-direct-contact"
      >
        <a
          href="tel:+81468747156"
          className="flex items-center gap-3 px-4 py-4 min-h-[56px] active:bg-white"
          data-testid="mobile-direct-phone"
        >
          <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white border border-[#E2E8F0] shrink-0">
            <Phone className="h-4 w-4 text-[#0F1D2D]" strokeWidth={1.6} />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.22em] font-mono-display uppercase text-[#64748B] font-semibold">
              {isJa ? "電話" : "Phone"}
            </div>
            <div className="text-[14px] font-bold tracking-tight text-[#0F1D2D]">
              +81 046-874-7156
            </div>
          </div>
        </a>
        <a
          href="mailto:support@nojoenterprise.com"
          className="flex items-center gap-3 px-4 py-4 min-h-[56px] active:bg-white"
          data-testid="mobile-direct-email"
        >
          <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white border border-[#E2E8F0] shrink-0">
            <Mail className="h-4 w-4 text-[#0F1D2D]" strokeWidth={1.6} />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.22em] font-mono-display uppercase text-[#64748B] font-semibold">
              {isJa ? "メール" : "Email"}
            </div>
            <div className="text-[14px] font-bold tracking-tight text-[#0F1D2D] break-anywhere">
              support@nojoenterprise.com
            </div>
          </div>
        </a>
        <div className="flex items-center gap-3 px-4 py-4 min-h-[56px]">
          <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white border border-[#E2E8F0] shrink-0">
            <MapPin className="h-4 w-4 text-[#0F1D2D]" strokeWidth={1.6} />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.22em] font-mono-display uppercase text-[#64748B] font-semibold">
              {isJa ? "本社" : "Office"}
            </div>
            <div className="text-[14px] font-bold tracking-tight text-[#0F1D2D]">
              {isJa ? "神奈川県横須賀市" : "Yokosuka, Kanagawa"}
            </div>
          </div>
        </div>
      </div>

      {/* Spacer so content above the sticky bottom bar isn't cut off */}
      <div className="h-4" aria-hidden />

      {/* 6) Sticky bottom CTA bar */}
      <div
        className="fixed bottom-0 inset-x-0 z-20 bg-white/95 backdrop-blur border-t border-[#E2E8F0] lg:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-bottom-bar"
      >
        <div className="grid grid-cols-3">
          <Link
            to={link("/services")}
            onClick={onClose}
            data-testid="mobile-bottom-services"
            className="flex flex-col items-center justify-center gap-1 min-h-[64px] text-[#0E5FB0] active:bg-[#EFF6FF]"
          >
            <Compass className="h-4 w-4" strokeWidth={2} />
            <span className="text-[11px] font-bold tracking-[0.06em] uppercase">
              {isJa ? "サービス" : "Services"}
            </span>
          </Link>
          <Link
            to={link("/contact")}
            onClick={onClose}
            data-testid="mobile-bottom-contact"
            className="flex flex-col items-center justify-center gap-1 min-h-[64px] bg-[#0E5FB0] text-white active:bg-[#084785]"
          >
            <MessageCircle className="h-4 w-4" strokeWidth={2} />
            <span className="text-[11px] font-bold tracking-[0.06em] uppercase">
              {isJa ? "お問い合わせ" : "Contact"}
            </span>
          </Link>
          <button
            type="button"
            data-testid="mobile-bottom-top"
            onClick={() => {
              const el = document.querySelector('[data-testid="mobile-menu"]');
              if (el) el.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex flex-col items-center justify-center gap-1 min-h-[64px] text-[#5B2E83] active:bg-[#F5F0FA]"
          >
            <ArrowUp className="h-4 w-4" strokeWidth={2} />
            <span className="text-[11px] font-bold tracking-[0.06em] uppercase">
              {isJa ? "上へ" : "Top"}
            </span>
          </button>
        </div>
      </div>

      {/* Subtle "Sparkles" accent reserved (lint-safe) */}
      <span className="hidden">
        <Sparkles aria-hidden />
        <Map aria-hidden />
      </span>
    </div>
  );
}
